import { Col, Row, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './editOrganisationMap.module.scss';
import {
  EditOrganisationMapEnum,
  OrganisationMapMode,
} from 'enums/organisationMapEnums';
import { generateNodesAndEdges } from '../OrganisationReactFlow/organisationReactFlow.util';
import COButton from 'shared/components/COButton';
import { ButtonSize } from 'enums/ButtonEnums';
import { Form, Formik, FormikProps } from 'formik';
import { OrganisationMapPayload } from 'models/CompanySetup/organisationPayload.model';
import OrganisationService from 'services/OrganisationService/organisation.service';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';
import OrganisationMapReactFlow, {
  OrganizationMapReset,
} from '../OrganisationReactFlow';
import useRedirect from 'shared/utils/useRedirect';
import ConfirmationModal from 'views/CreateProductFlow/RawMaterial/ConfirmationModal';
import TabsComponent from 'shared/components/TabsComponent';
import { TabKeysEnums } from 'enums/TabKeysEnums';
import EditOrganisationMapModals from './OrganizationModals';
import { mapValidationSchema } from './saveMapValidation';
import { MetaData, OptionsMeta } from 'models/MetaData/metaData.model';
import {
  EditPageModals,
  updateOrganisationList,
} from './editOrganisationMap.util';
import { useRef } from 'react';
import useFilters from 'shared/hooks/useFilters';
import { CompanySetUpTabHash } from 'enums/CompanySetupEnums';
import AlertModal from './AlertModal';
import clsx from 'clsx';
import { year } from 'constants/constants';
import { deepClone } from 'shared/utils/deepCopy.util';
import OptionsDropdown from 'shared/components/OptionsDropdown';
import { stopBubbling } from 'shared/utils/stopBubbling';
import { MapResetButton } from '../OrganisationReactFlow/resetOrganizationMap';
import EditOrganisationHeader from './EditOrganisationHeader';
import {
  getResetModalConfig,
  handleSubmitUtil,
} from './editOrganisationMapUtils';
import OrganisationMapService from 'services/OrganisationService/organisationMap.service';
import { useUserRole } from 'shared/hooks/useUserRole';
import { isViewer, isDataContributor } from 'shared/utils/roleUtils';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';

const EditOrganisationMap = () => {
  const [activeYear, setActiveYear] = useState('');
  const [isYearModalOpen, setIsYearModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [deleteOption, setDeleteOption] = useState<boolean>(false);
  const { redirectToCompanySetUp, redirectToEditOrganization } = useRedirect();
  const { getParams, setParams } = useFilters();
  const queryParamsList = [year];
  const { year: activeTabYear } = getParams(queryParamsList);
  const { organizationData } = useParentOrganizationContext();
  const mapRef = useRef<OrganizationMapReset>(null);
  const userRole = useUserRole();
  const isViewerRole = isViewer(userRole);
  const isDataContributorRole = isDataContributor(userRole);
  const canEditMap = !isViewerRole && !isDataContributorRole;

  const {
    fetchOrganisationListMeta,
    validateOrganisationStatus,
    organisationsList,
    loading,
    fetchOrganisationYearsMeta,
    organisationYearsList,
  } = OrganisationService();

  const {
    fetchOrganisationMap,
    organisationMap: initalValue,
    updateOrganisationMap,
    deleteYear,
    createOrganisationMap,
  } = OrganisationMapService();

  useEffect(() => {
    fetchOrganisationListMeta();
    fetchOrganisationYearsMeta();
  }, [activeTabYear]);

  useEffect(() => {
    if (organisationYearsList?.length) {
      setActiveYear(activeTabYear?.toString());
      fetchOrganisationMap({ year: Number(activeTabYear) });
    }
  }, [organisationYearsList, activeTabYear]);

  const [organisationMapData, setOrganisationMapData] =
    useState<OrganisationMapPayload>(new OrganisationMapPayload());

  const [organisationListMeta, setOrganisationListMeta] = useState<MetaData[]>(
    []
  );

  const [openModalType, setOpenModalType] = useState<EditPageModals>();

  useEffect(() => {
    const orgData = initalValue && deepClone(initalValue);
    orgData && setOrganisationMapData(orgData);
  }, [initalValue]);

  useEffect(() => {
    const orgList = organisationsList && deepClone(organisationsList);
    orgList && setOrganisationListMeta(organisationsList);
  }, [organisationsList]);

  const formRef = useRef<FormikProps<OrganisationMapPayload>>(null);

  const { nodes, edges } = useMemo(() => {
    const { organizationMap } = organisationMapData;
    const updatedOrganisationListMeta: MetaData[] = updateOrganisationList(
      organisationListMeta,
      organizationMap ?? []
    );
    setOrganisationListMeta(updatedOrganisationListMeta);
    return generateNodesAndEdges({
      data: organisationMapData.organizationMap ?? [],
      mode: OrganisationMapMode.EDIT,
      organisationListsMeta: updatedOrganisationListMeta,
      year: organisationMapData.year,
      validateOrganisationStatus,
      setOrganisationMapData: canEditMap ? setOrganisationMapData : undefined,
      canEdit: canEditMap,
    });
  }, [organisationMapData]);

  const handleSave = async (values: OrganisationMapPayload) => {
    if (!canEditMap) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    await updateOrganisationMap(values);
  };

  const closeModals = () => setOpenModalType(undefined);

  const openModal = (modalType: EditPageModals) => () =>
    setOpenModalType(modalType);

  const resetModalConfig = getResetModalConfig(openModalType, closeModals);

  const handleReset = () => {
    if (!canEditMap) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setDeleteOption(false);
    formRef.current?.resetForm();
    const orgData = initalValue && deepClone(initalValue);
    orgData && setOrganisationMapData(orgData);
    const orgList = organisationsList && deepClone(organisationsList);
    orgList && setOrganisationListMeta(organisationsList);
    closeModals();
  };

  const handleDeleteYear = (index: number, year?: string) => async () => {
    if (year) {
      const response = await deleteYear(year);
      if (!response) return;
      const isCurrentYear = activeTabYear == year;
      if (isCurrentYear && organisationYearsList?.length) {
        const redirectionIndex = index ? 0 : 1;
        const year =
          organisationYearsList[redirectionIndex].value?.toString() ?? '';
        setParams({ year });
        setActiveYear(year);
      } else {
        await fetchOrganisationYearsMeta();
        await fetchOrganisationListMeta();
      }
    }
  };

  const getYearsLabel = (year: OptionsMeta, index: number) => {
    const deleteModalprops = {
      deleteTitle: EditOrganisationMapEnum.DELETE_YEAR_TEXT,
      onDelete: handleDeleteYear(index, year.value?.toString()),
      loading,
    };
    return (
      <Row align="middle">
        <Col>{year.value?.toString()}</Col>
        {organisationYearsList?.length > 1 ? (
          <Col onClick={stopBubbling}>
            <OptionsDropdown
              className={styles.activeTabDropdown}
              deleteModalprops={deleteModalprops}
              {...(deleteOption && {
                deleteHandler: () =>
                  openModal(EditPageModals.UNSAVED_CHANGES_MODAL)(),
              })}
            />
          </Col>
        ) : null}
      </Row>
    );
  };

  const yearsTabs = organisationYearsList?.map((year, index) => {
    return {
      key: year?.value?.toString(),
      label: getYearsLabel(year, index),
      children: (
        <div className={styles.editOrganisationChildrenWrapper}>
          {initalValue?.organizationMap?.length ? (
            <Formik
              innerRef={formRef}
              initialValues={initalValue}
              onSubmit={handleSave}
              validationSchema={mapValidationSchema}
              enableReinitialize>
              {({ errors, dirty }) => {
                setDeleteOption(!!formRef?.current?.dirty);
                return (
                  <Form>
                    <Row className={styles.organisationMapWrapper}>
                      <Col span={24} className={styles.buttonsContainer}>
                        <MapResetButton mapRef={mapRef} />
                        <div className={styles.buttons}>
                          <COButton
                            text
                            disabled={!dirty || !canEditMap}
                            onClick={openModal(EditPageModals.RESET_MODAL)}>
                            {EditOrganisationMapEnum.RESET_BUTTON_TEXT}
                          </COButton>
                          <COButton
                            size={ButtonSize.LARGE}
                            loading={loading}
                            submit
                            disabled={!dirty || !canEditMap}>
                            {EditOrganisationMapEnum.SAVE_BUTTON_TEXT}
                          </COButton>
                        </div>
                      </Col>
                      <Col
                        span={24}
                        className={clsx(styles.organisationMapError, {
                          [styles.visible]: !!errors.organizationMap,
                        })}>
                        *Please add boundary shares to all organizations before
                        saving
                      </Col>
                      <Col span={24} className={styles.organisationMap}>
                        {nodes.length ? (
                          <OrganisationMapReactFlow
                            initialNodes={nodes}
                            initialEdges={edges}
                            ref={mapRef}
                          />
                        ) : null}
                      </Col>
                    </Row>
                    <ConfirmationModal
                      {...resetModalConfig}
                      onConfirm={handleReset}
                    />
                  </Form>
                );
              }}
            </Formik>
          ) : null}
        </div>
      ),
    };
  });
  const handleTabChange = (key: string) => {
    if (formRef.current?.dirty) {
      openModal(EditPageModals.UNSAVED_CHANGES_MODAL)();
      return;
    }
    if (key === TabKeysEnums.ADD_YEAR) {
      if (!canEditMap) {
        Notification({
          message: "You don't have admin access to add location and year",
          description: '',
          type: NotificationTypes.WARNING,
        });
        return;
      }
      setIsYearModalOpen(true);
    } else {
      setActiveYear(key);
      redirectToEditOrganization(key);
      fetchOrganisationMap({ year: Number(key) });
    }
  };

  const handleCloseModal = () => {
    if (isYearModalOpen) {
      setIsYearModalOpen(false);
    } else {
      setIsDuplicateModalOpen(false);
    }
  };
  const handleModalOpen = () => {
    if (!canEditMap) {
      Notification({
        message: "You don't have admin access to add location and year",
        description: '',
        type: NotificationTypes.WARNING,
      });
      return;
    }
    setIsDuplicateModalOpen(true);
  };

  const handleSubmit = async (values?: { year?: string }) => {
    await handleSubmitUtil(
      values,
      isYearModalOpen,
      createOrganisationMap,
      fetchOrganisationMap,
      setActiveYear,
      setParams,
      handleCloseModal,
      formRef,
      setOrganisationMapData
    );
  };

  const handleBack = () => {
    formRef.current?.dirty
      ? openModal(EditPageModals.UNSAVED_CHANGES_MODAL)()
      : redirectToCompanySetUp(CompanySetUpTabHash.ORG_MAP, {
          year: activeTabYear,
        })();
  };

  return (
    <Spin
      spinning={loading || !initalValue}
      className={styles.loadingContainer}>
      <div className={styles.editOrganisationWrapper}>
        <EditOrganisationHeader
          handleBack={handleBack}
          handleModalOpen={handleModalOpen}
          organisationYearsList={organisationYearsList}
          organisationMapData={organisationMapData}
          organizationData={organizationData}
          canEditMap={canEditMap}
        />
        <div className={styles.tabsContainer}>
          <TabsComponent
            haveAddButton={canEditMap}
            onTabChange={handleTabChange}
            activeTab={activeYear}
            tabs={yearsTabs ? yearsTabs : []}
            destroyInactiveTabPane
          />
          <EditOrganisationMapModals
            handleCloseModal={handleCloseModal}
            handleSubmit={handleSubmit}
            isDuplicateModalOpen={isDuplicateModalOpen}
            isYearModalOpen={isYearModalOpen}
            loading={loading}
            activeYear={activeYear}
            organisationYearsList={organisationYearsList}
          />
          <AlertModal
            visible={openModalType === EditPageModals.UNSAVED_CHANGES_MODAL}
            onClose={closeModals}
          />
        </div>
      </div>
    </Spin>
  );
};

export default EditOrganisationMap;
